import React, { useState } from "react"
import { CollapsableFilter } from "./collapsable_filter"
import { FilterSearchInput } from "./filter_search_input"
import { IncludeExcludeTag } from "./include_exclude_tag"
import { useDisplayOrder, toggleIncludeExclude, removeFromBoth } from "./filter-utils"

const t = (key: string) => window.i18n.t(key)

interface KeywordsFilterProps {
  includedValues: string[]
  excludedValues: string[]
  operator: "and" | "or"
  onChangeIncluded: (values: string[]) => void
  onChangeExcluded: (values: string[]) => void
  onChangeOperator: (operator: "and" | "or") => void
}

export function KeywordsFilter({
  includedValues = [],
  excludedValues = [],
  operator = "and",
  onChangeIncluded,
  onChangeExcluded,
  onChangeOperator,
}: KeywordsFilterProps) {
  const [inputValue, setInputValue] = useState("")

  const allSelectedSet = new Set([...includedValues, ...excludedValues])
  const hasValues = allSelectedSet.size > 0
  const hasMultipleValues = allSelectedSet.size > 1
  const allExcluded = excludedValues.length > 0 && includedValues.length === 0

  // Use shared hook for stable display order
  const allValues = useDisplayOrder(includedValues, excludedValues)

  const handleAddKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault()
      // Sanitize: only allow letters, numbers, and periods
      const sanitized = inputValue.replace(/[^\p{L}0-9.]/gu, " ").trim()
      if (sanitized && !allSelectedSet.has(sanitized)) {
        onChangeIncluded([...includedValues, sanitized])
      }
      setInputValue("")
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      // Sanitize: only allow letters, numbers, and periods
      const sanitized = inputValue.replace(/[^\p{L}0-9.]/gu, " ").trim()
      if (sanitized && !allSelectedSet.has(sanitized)) {
        onChangeIncluded([...includedValues, sanitized])
      }
      setInputValue("")
    }
  }

  const handleRemoveKeyword = (keyword: string) => {
    removeFromBoth(keyword, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleToggleIncludeExclude = (keyword: string) => {
    toggleIncludeExclude(keyword, includedValues, excludedValues, onChangeIncluded, onChangeExcluded)
  }

  const handleClear = () => {
    onChangeIncluded([])
    onChangeExcluded([])
    setInputValue("")
  }

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.keywords.title")}
      icon="hashtag"
      tooltip={t("js.back.discover.filters.keywords.tooltip")}
      count={allSelectedSet.size}
      hasValues={hasValues}
      onClear={handleClear}
    >
      <FilterSearchInput
        id="keywords-input"
        label={t("js.back.discover.filters.keywords.title")}
        placeholder={t("js.back.discover.filters.keywords.placeholder")}
        value={inputValue}
        onChange={setInputValue}
        onKeyDown={handleAddKeyword}
        onBlur={handleBlur}
        className="h-input h-input--block mb-2"
      />

      {hasMultipleValues && (
        <nav className={`h-pill-tabs h-pill-tabs--sm h-pill-tabs--full mt-1${allExcluded ? " disabled" : ""}`}>
          <button
            type="button"
            className={`h-pill-tabs__link${operator === "and" ? " is-active" : ""}`}
            onClick={() => !allExcluded && onChangeOperator("and")}
            disabled={allExcluded}
            data-controller="tooltip"
            data-bs-trigger="hover"
            title={t("js.back.discover.filters.match_all_tooltip")}
          >
            <span className="h-pill-tabs__name">{t("js.back.discover.filters.match_all")}</span>
          </button>
          <button
            type="button"
            className={`h-pill-tabs__link${operator === "or" ? " is-active" : ""}`}
            onClick={() => !allExcluded && onChangeOperator("or")}
            disabled={allExcluded}
            data-controller="tooltip"
            data-bs-trigger="hover"
            title={t("js.back.discover.filters.match_any_tooltip")}
          >
            <span className="h-pill-tabs__name">{t("js.back.discover.filters.match_any")}</span>
          </button>
        </nav>
      )}

      {hasValues && (
        <div className="mt-2">
          {allValues.map((keyword) => (
            <IncludeExcludeTag
              key={keyword}
              label={keyword}
              isIncluded={includedValues.includes(keyword)}
              onToggle={() => handleToggleIncludeExclude(keyword)}
              onRemove={() => handleRemoveKeyword(keyword)}
            />
          ))}
        </div>
      )}
    </CollapsableFilter>
  )
}
